import { fileDownload } from "./file";
import mtabApi from "./mtabApi";

const ARTWAREHOUSE_SORT_MAP = {
  warehouseName: "WarehousName",
  type: "Type",
  inventoryNo: "InventoryNo",
  serialNumber: "SerialNumber",
  title: "ArtworkTitle",
  artist: "Artist",
  typeOfArt: "TypeOfArt",
  orderInDate: "OrderInDate"
};

function normalizeArtWarehouseObject(item) {
  const images = Array.isArray(item?.Images) ? item.Images : [];
  const documents = Array.isArray(item?.Documents) ? item.Documents : [];
  const mainImageId = item?.MainImageID || "";
  const getArtWarehouseImagePath = (image) => {
    const filename = image?.FilenameSaved || image?.FileName;
    return filename ? `Images/${filename}` : "";
  };
  const getArtWarehouseDocumentPath = (document) => {
    const filename = document?.FileNameSaved || document?.FileName;
    return filename ? `Files/${filename}` : "";
  };

  const sortedImages = [...images].sort((a, b) => {
    const aIsMain = a?.ImageID && a.ImageID === mainImageId ? 1 : 0;
    const bIsMain = b?.ImageID && b.ImageID === mainImageId ? 1 : 0;
    return bIsMain - aIsMain;
  });

  const imageUrls = sortedImages
    .map((img) => ({
      imagePath: getArtWarehouseImagePath(img),
      originalName: img?.FileName || ""
    }))
    .filter(({ imagePath }) => imagePath)
    .map(({ imagePath, originalName }) =>
      fileDownload(imagePath, "art-warehouse", originalName)
    );

  const normalizedDocuments = documents.map((doc) => ({
    ...doc,
    file: doc?.FileName || doc?.FileNameSaved || "Document",
    FileNameOrg: doc?.FileName || doc?.FileNameSaved || "Document",
    Url: getArtWarehouseDocumentPath(doc),
    service: "art-warehouse",
    columns: {
      type: doc?.FileType || "-",
      order: ""
    },
    Notes: doc?.Description || ""
  }));

  return {
    objectId: item?.ObjectID || "",
    companyId: item?.CompanyID || 0,
    serialNumber: item?.SerialNumber || "",
    warehouseName: item?.WarehousName || "",
    parentObjectId: item?.ParentObjectID || "",
    artworkTitle: item?.ArtworkTitle || "",
    containerTitle: item?.ContainerTitle || "",
    type: item?.Type || "",
    orderInDate: item?.OrderInDate ? item.OrderInDate.slice(0, 10) : "",
    customerNote: item?.CustomerNote || "",
    dimensions: item?.Dimensions || "",
    weight: item?.Weight || 0,
    noOfParts: item?.NoOfParts || 0,
    artist: item?.Artist || "",
    provenance: item?.Provenance || "",
    ed: item?.Ed || "",
    inventoryNo: item?.InventoryNo || "",
    typeOfArt: item?.TypeOfArt || "",
    customsGNumber: item?.CustomsGNumber || "",
    customsIncomingDocument: item?.CustomsIncomingDocument || "",
    customsOutgoingDocument: item?.CustomsOutgoingDocument || "",
    mainImageId,
    imageUrls,
    documents: normalizedDocuments,
    sourceCreated: item?.SourceCreated || ""
  };
}

const executeArtWarehouseObjectsQuery = async ({
  params,
  token,
  page,
  pageSize
}) => {
  const res = await mtabApi({
    url: "/api/artwarehouse/Object/multiple",
    method: "GET",
    params,
    headers: {
      Authorization: `token ${token}`,
      "content-type": "application/json"
    }
  });

  const payload = res?.data || {};
  const rows = Array.isArray(payload?.data) ? payload.data : [];

  return {
    items: rows.map(normalizeArtWarehouseObject),
    page: Number(payload?.currentPage) || page,
    pageSize: Number(payload?.pageSize) || pageSize,
    totalItems: Number(payload?.totalCount) || rows.length,
    totalPages: Math.max(1, Number(payload?.totalPages) || 1)
  };
};

/**
 *
 * @param {{
 * companyId: string|number,
 * token?: string,
 * page?: number,
 * pageSize?: number,
 * search?: string,
 * sortKey?: string,
 * sortDirection?: "asc"|"desc"
 * }} params
 * @returns {Promise<{items: Array, page: number, pageSize: number, totalItems: number, totalPages: number}>}
 */
const getArtWarehouseObjects = async ({
  companyId,
  token,
  page = 1,
  pageSize = 25,
  search = "",
  sortKey = "warehouseName",
  sortDirection = "asc"
}) => {
  if (!companyId || !token) {
    return {
      items: [],
      page: 1,
      pageSize,
      totalItems: 0,
      totalPages: 1
    };
  }

  const trimmedSearch = search.trim();
  const effectiveSearch = trimmedSearch.length > 0 ? trimmedSearch : "";
  const sortBy = ARTWAREHOUSE_SORT_MAP[sortKey];

  const params = {
    companyId: Number(companyId),
    page,
    pageSize,
    isDescending: sortDirection === "desc"
  };

  if (sortBy) {
    params.sortBy = sortBy;
  }

  if (effectiveSearch) {
    params.q = effectiveSearch;
  }

  try {
    const result = await executeArtWarehouseObjectsQuery({
      params,
      token,
      page,
      pageSize
    });

    return result;
  } catch (error) {
    console.error(
      "getArtWarehouseObjects error:",
      error?.response?.data?.message || error
    );

    return {
      items: [],
      page,
      pageSize,
      totalItems: 0,
      totalPages: 1
    };
  }
};

/**
 *
 * @param {{companyId?: string|number, token?: string, objectId: string}} params
 * @returns {Promise<object|null>}
 */
const getArtWarehouseObjectById = async ({ companyId, token, objectId }) => {
  if (!companyId || !token || !objectId) return null;

  try {
    const res = await mtabApi({
      url: `/api/artwarehouse/Object/single/${objectId}`,
      method: "GET",
      headers: {
        Authorization: `token ${token}`,
        "content-type": "application/json"
      }
    });

    const row = res?.data;
    if (!row) return null;

    if (Number(row?.CompanyID) !== Number(companyId)) {
      console.warn(
        "Blocked art object from different company",
        row?.CompanyID,
        companyId
      );
      return null;
    }

    return normalizeArtWarehouseObject(row);
  } catch (error) {
    console.error(
      "getArtWarehouseObjectById error:",
      error?.response?.data?.message || error
    );
    return null;
  }
};

const getArtWarehouseChildObjects = async ({
  token,
  parentObjectId
}) => {
  if (!token || !parentObjectId) return [];

  try {
    const res = await mtabApi({
      url: "/api/artwarehouse/Object/children",
      method: "GET",
      params: {
        parentObjectId
      },
      headers: {
        Authorization: `token ${token}`,
        "content-type": "application/json"
      }
    });

    const rows = Array.isArray(res?.data?.data) ? res.data.data : [];

    return rows.map(normalizeArtWarehouseObject);
  } catch (error) {
    console.error(
      "getArtWarehouseChildObjects error:",
      error?.response?.data?.message || error
    );
    return [];
  }
};

export {
  getArtWarehouseChildObjects,
  getArtWarehouseObjectById,
  getArtWarehouseObjects
};
